import {
  IconChevronArrowStyle,
  IconChevronArrowStyleWhite,
  ReferOutButtonContainer,
} from "@/partial/campaign/campaign-form/styles";
import {
  CHANNEL,
  DATA_SOURCE_TYPE,
  fieldCategories,
  MAIN_OPERATOR,
  optionsCondition,
  SCHEDULE_MODE,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import React, { Fragment, useEffect } from "react";
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from "react-hook-form";
import { Button, IconAdd } from "tvs-unified-lib";
import Channel from "./components/channel";
import ReferOut from "./components/refer-out";
import useCampaignStore from "@/store/campaignStore";
import { SelectOptionType } from "@/partial/template/types";

interface CampaignChannelFormProps {
  isEdit: boolean;
  control: Control<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
  watch: UseFormWatch<CampaignForm>;
}

export const CampaignChannelForm = ({
  isEdit,
  control,
  setValue,
  watch,
}: CampaignChannelFormProps) => {
  const {
    optionsConditionChannel,
    setOptionsConditionChannel,
    setOptionsAdditionalParameters,
    campaignDetail: campaignDetailStore,
  } = useCampaignStore();
  const {
    fields: campaignChannelFields,
    append: appendCampaignChannel,
    remove: removeCampaignChannel,
  } = useFieldArray({
    control,
    name: "campaign_channels",
  });

  const mainOperator = (index: number) =>
    watch(`campaign_channels.${index}.main_operator`) || MAIN_OPERATOR.AND;

  const dataSource = useWatch({
    control,
    name: `data_source` as `data_source`,
  });
  const dataFile = useWatch({
    control,
    name: `data_file` as `data_file`,
  });

  const processDateFields = (
    headerMapping: Record<string, string>,
    excelHeaders: string[]
  ) => {
    const dateFields = Object.entries(headerMapping)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value.startsWith("Date"))
      .map(([key]) => key);

    const finalResult = dateFields.map((field) => {
      const formattedField = field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());

      const matchingLabel = excelHeaders.find(
        (header: string) =>
          header.toLowerCase() === formattedField.toLowerCase()
      );

      return {
        value: matchingLabel || formattedField,
        label: matchingLabel || formattedField,
      };
    });

    return finalResult;
  };

  type FieldCategory = "String" | "Date" | "Numeric" | "List";

  const processFieldCategories = (
    headerMapping: Record<string, string>,
    excelHeaders: string[],
    mandatoryFields: string[]
  ) => {
    // Define the categories and their matching rules
    const categoryRules: Record<FieldCategory, (val: string) => boolean> = {
      String: (val) => val.startsWith("String"),
      Date: (val) => val.startsWith("Date"),
      Numeric: (val) =>
        val.startsWith("Float") ||
        val.startsWith("Amount") ||
        val.startsWith("Integer"),
      List: (val) => val.startsWith("List"),
    };

    // Initialize result
    const result: Record<FieldCategory, { value: string; label: string }[]> = {
      String: [],
      Date: [],
      Numeric: [],
      List: [],
    };

    // Go through each mapping and categorize
    for (const [key, type] of Object.entries(headerMapping)) {
      const matchedCategory = (
        Object.keys(categoryRules) as FieldCategory[]
      ).find((cat) => categoryRules[cat](type));

      if (!matchedCategory) continue;

      const formattedField = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());

      const matchingLabel = excelHeaders.find(
        (header) => header.toLowerCase() === formattedField.toLowerCase()
      );

      const labelValue = matchingLabel || formattedField;

      if (mandatoryFields.includes(labelValue)) {
        result[matchedCategory].push({
          value: matchingLabel || formattedField,
          label: matchingLabel || formattedField,
        });
      }
    }

    return result;
  };

  useEffect(() => {
    if (
      dataSource?.[0]?.value === DATA_SOURCE_TYPE.DATA_UPLOAD &&
      dataFile &&
      dataFile[0]?.other?.headerMapping
    ) {
      const headerMapping = dataFile[0]?.other?.headerMapping;
      const excelHeaders = dataFile[0]?.other?.excelHeaders;
      const result = processDateFields(headerMapping, excelHeaders);
      const mandatoryFields: string[] =
        dataFile[0]?.other?.user_field_mapping?.mandatoryFields ?? [];

      const additionalParamsOptions = processFieldCategories(
        headerMapping,
        excelHeaders,
        mandatoryFields
      );
      setOptionsAdditionalParameters(additionalParamsOptions);
      setOptionsConditionChannel(result);
    } else {
      setOptionsConditionChannel(optionsCondition);
      setOptionsAdditionalParameters(fieldCategories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFile, dataSource]);

  useEffect(() => {
    if (campaignDetailStore && optionsConditionChannel) {
      campaignDetailStore.campaignChannels.forEach(
        (channel: any, index: number) => {
          const condition = optionsConditionChannel.find(
            (item: SelectOptionType) => item.value === channel.condition
          );
          if (condition) {
            setValue(
              `campaign_channels.${index}.condition` as `campaign_channels.${number}.condition`,
              [condition]
            );
          }
        }
      );
    }
  }, [campaignDetailStore, optionsConditionChannel, setValue]);

  const handleAddChannel = () => {
    appendCampaignChannel({
      channel: CHANNEL.SMS,
      trigger_by: [],
      condition: [],
      action: [],
      specific_day_check: undefined,
      number_of_day_check: undefined,
      is_check_day: SCHEDULE_MODE.SPECIFIC_DATE,
      main_operator: MAIN_OPERATOR.AND,
      [`campaign_data_channels`]: [
        {
          day: [],
          time_band: [],
          template_script_id: [],
        },
      ],
      [`refer_out`]: [
        {
          type: "",
          when: [],
          condition: [],
          current_operator: MAIN_OPERATOR.OR,
        },
      ],
    });
  };

  const handleChangeMainOperator = (
    operatorType: "AND" | "OR",
    targetIndex: number
  ) => {
    setValue(
      `campaign_channels.${targetIndex}.main_operator` as `campaign_channels.${number}.main_operator`,
      operatorType
    );
  };

  return (
    <React.Fragment>
      {campaignChannelFields.map((field, index) => (
        <Fragment key={field.id}>
          {index > 0 && (
            <React.Fragment>
              <ReferOutButtonContainer>
                <Button
                  variant={
                    mainOperator(index) === MAIN_OPERATOR.AND
                      ? "block"
                      : "outline"
                  }
                  type="button"
                  size="sm"
                  label="And"
                  onClick={() => handleChangeMainOperator("AND", index)}
                />
                <Button
                  variant={
                    mainOperator(index) === MAIN_OPERATOR.OR
                      ? "block"
                      : "outline"
                  }
                  type="button"
                  size="sm"
                  label="Or"
                  icon={
                    mainOperator(index) === MAIN_OPERATOR.OR ? (
                      <IconChevronArrowStyleWhite />
                    ) : (
                      <IconChevronArrowStyle />
                    )
                  }
                  iconPosition="right"
                  onClick={() => handleChangeMainOperator("OR", index)}
                />
              </ReferOutButtonContainer>
              {mainOperator(index) === MAIN_OPERATOR.OR && (
                <ReferOut
                  indexChannel={index}
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  mainOperator={mainOperator(index)}
                />
              )}
            </React.Fragment>
          )}
          <Channel
            isEdit={isEdit}
            control={control}
            indexChannel={index}
            remove={removeCampaignChannel}
            setValue={setValue}
            watch={watch}
          />
        </Fragment>
      ))}

      <Button
        label="Channel"
        className="button-add-channel"
        variant="outline"
        iconPosition="left"
        onClick={handleAddChannel}
        icon={<IconAdd fill="#3548c9" />}
      />
    </React.Fragment>
  );
};

export default CampaignChannelForm;
